/**
 * Helpers for reading a /cost_matrix result.
 *
 * `hasAnyComputedTravelCost` answers "did this response actually populate
 * anything?" — used to tell a genuinely-empty matrix (e.g. the road-network
 * engine returned nothing) apart from a real one, so the UI can warn instead of
 * reporting a false "successfully populated" over blank cells.
 *
 * A row counts as computed if it carries a distance or a duration; `null`,
 * `undefined` and `''` are all "no value". A legitimate `0` (same-location pair)
 * counts as a value, so an all-zero matrix is NOT treated as empty here — that's
 * a distinct backend concern (a silent ORS failure once served zeros) and is not
 * something the front end can reliably disambiguate from real zero-distance rows.
 */
import { multipliersOff } from 'store/functions/costMethods.js';

/** A row from a location to itself (both ids known and equal): not a trip. */
export const isSelfPair = (row) => Boolean(row)
  && row.fromLocationId !== undefined && row.fromLocationId !== null
  && row.fromLocationId === row.toLocationId;

export function hasAnyComputedTravelCost(rows) {
  if (!Array.isArray(rows)) return false;
  const has = (v) => v !== null && v !== undefined && v !== '';
  // A location to itself is 0 in any calculation, so it proves nothing was
  // calculated: an all-failed response still carries those zeros.
  return rows.some((row) => row && !isSelfPair(row) && (has(row.distance) || has(row.duration)));
}

/**
 * A row is "flagged" when the backend marked it a warning (`Valid === 'warning'`,
 * i.e. computed with a fallback — e.g. an unreachable location estimated
 * straight-line) or an error (`Valid === false`). A freshly-populated matrix
 * always carries an explicit `Valid`, so `undefined` (an unvalidated/manual row)
 * deliberately does NOT count.
 */
export function isFlaggedTravelCostRow(row) {
  return Boolean(row) && (row.Valid === 'warning' || row.Valid === false);
}

/** How many rows the backend flagged (warning or error). */
export function countFlaggedTravelCostRows(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter(isFlaggedTravelCostRow).length;
}

/**
 * True when a travel-cost matrix has rows but not a single number in any of
 * them: every cost, distance and duration is empty.
 *
 * That is the pair skeleton the tables build as locations are added, before
 * anything has been calculated or entered. The solver has nothing to route
 * with, yet a /problem request carrying it still "succeeds", returning routes
 * that mean nothing. From 2026-08-29, 25 real website solves were submitted
 * exactly like that, and 19 were shown to people as successful plans.
 *
 * An empty matrix (no rows) is not "uncosted": there is nothing to route.
 * A partly filled one is not either; see the note on the solve guard.
 *
 * @param {Array<object>} rows - data.travel_costs, or ProblemData.travelCosts.
 * @returns {boolean}
 */
export function isUncostedTravelCostMatrix(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const has = (v) => v !== null && v !== undefined && v !== '';
  // Only trips between different locations count. A matrix whose only values
  // are self-pair zeros is still empty; a matrix with no such trips (one
  // location) has nothing to route and is not "uncosted".
  const trips = rows.filter((row) => row && !isSelfPair(row));
  if (trips.length === 0) return false;
  return trips.every((row) => !has(row.cost) && !has(row.distance) && !has(row.duration));
}

/**
 * What a person sees when a solve is stopped because nothing was calculated.
 * Says what to do in both situations that lead here: costs were never
 * calculated, or calculating them failed (most often because the road-network
 * method isn't in their plan).
 */
export const UNCOSTED_MATRIX_MESSAGE =
  'The travel costs between your locations are empty, so there is nothing to plan routes with. ' +
  'Fill them in with “Populate travel costs” on the Travel Costs table. ' +
  'If that fails, open Cost settings and choose a straight-line distance method.';

const hasValue = (v) => v !== null && v !== undefined && v !== '';
const pairKey = (row) => `${row && row.fromLocationId}\u0000${row && row.toLocationId}`;
const COST_FIELDS = ['cost', 'distance', 'duration'];

/**
 * Which travel-cost fields a scenario's solve depends on.
 *
 * Cost and travel time, always: the solver reads both for every trip and treats
 * an empty value in either as a trip it can't make, so a pair with no travel time
 * leaves stops unassigned even when no time window is set (a website run on
 * 2026-09-15 assigned 0 of 7 stops that way), and a pair with a typed distance
 * but no cost is just as unusable. Plus distance for a distance metric, since
 * its cost is calculated from it.
 *
 * `needDistance` comes from distanceCalculationNeeded in inputReducer, passed in
 * because this module is imported by inputReducer.
 *
 * @param {object} args
 * @param {string} args.costMetric
 * @param {boolean} args.needDistance
 * @returns {string[]}
 */
export function travelCostFieldsNeeded({ costMetric, needDistance }) {
  if (costMetric === 'custom') return ['cost', 'duration'];
  // Cost too: it is what the solver routes on. It is calculated from the
  // metric's value, so an empty one is always fillable.
  return needDistance ? ['distance', 'cost', 'duration'] : ['cost', 'duration'];
}

const FIELD_WORDS = { cost: 'travel cost', distance: 'distance', duration: 'travel time' };

/**
 * The fields actually empty among the missing rows, in words: "travel time",
 * "distance or travel time". So a matrix missing only travel times says exactly
 * that, rather than naming everything that could have been missing.
 *
 * @param {Array<object>} missingRows - from summarizeTravelCostGaps
 * @param {string[]} fields - the needed fields
 * @returns {string}
 */
export function missingFieldsPhrase(missingRows, fields) {
  const empty = emptyFields(missingRows, fields);
  const words = (empty.length ? empty : fields || []).map((f) => FIELD_WORDS[f]).filter(Boolean);
  if (words.length <= 1) return words[0] || 'travel costs';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

/**
 * The needed fields that are empty in at least one of the missing rows.
 *
 * @param {Array<object>} missingRows - from summarizeTravelCostGaps
 * @param {string[]} fields - the needed fields
 * @returns {string[]}
 */
export function emptyFields(missingRows, fields) {
  return (fields || []).filter((f) => (missingRows || []).some((row) => row && !hasValue(row[f])));
}

/**
 * Whether the app can calculate a field from coordinates: travel time always,
 * distance when the metric is distance. A custom cost only a person can enter.
 * The calculation for missing values (fillMissingOnly) keeps every value already
 * there, so offering it never replaces a number someone typed.
 *
 * @param {string} field
 * @param {string} costMetric
 * @returns {boolean}
 */
export function isCalculableField(field, costMetric) {
  if (field === 'duration') return true;
  if (field === 'distance') return costMetric === 'distance';
  if (field === 'cost') return costMetric === 'distance' || costMetric === 'duration';
  return false;
}

/**
 * How many location pairs the solver can use: a cost and a travel time, the two
 * values it reads. A location to itself doesn't count. With none, a solve can
 * assign no stops at all.
 *
 * @param {Array<object>} rows - data.travel_costs
 * @returns {number}
 */
export function countRoutablePairs(rows) {
  const routable = new Set();
  (rows || []).forEach((row) => {
    if (row && !isSelfPair(row) && hasValue(row.cost) && hasValue(row.duration)) routable.add(pairKey(row));
  });
  return routable.size;
}

/** What an empty value does to a solve, said the same way everywhere. */
export const MISSING_TRAVEL_COST_CONSEQUENCE =
  "The optimizer can't use a trip with a missing value, so stops that depend on those trips may be left unassigned.";

/** Why there is no "Solve anyway" when not a single trip can be used. */
export const NO_ROUTABLE_TRIPS_MESSAGE =
  "There's no “Solve anyway” because no trip has both a cost and a travel time yet, so a solve would assign no stops.";

/**
 * How complete a travel-cost matrix is, counted in location pairs.
 *
 * A pair is missing when any of its rows (one per vehicle type, with
 * vehicle-type costs) lacks a value in any of `fields`. A pair with a distance
 * but no duration is missing when duration is needed. A location to itself is not
 * a trip and is not counted.
 *
 * `partial` is the case Run asks about: some pairs filled and some missing. An
 * entirely empty matrix is not partial; enqueueOptimization handles that one
 * (see isUncostedTravelCostMatrix).
 *
 * @param {Array<object>} rows - data.travel_costs
 * @param {string[]} [fields] - from travelCostFieldsNeeded; defaults to "any value at all"
 * @returns {{ totalPairs: number, missingPairs: number, partial: boolean, missingRows: Array<object> }}
 */
export function summarizeTravelCostGaps(rows, fields) {
  const empty = { totalPairs: 0, missingPairs: 0, partial: false, missingRows: [] };
  if (!Array.isArray(rows) || rows.length === 0) return empty;
  const rowIsMissing = Array.isArray(fields) && fields.length > 0
    ? (row) => fields.some((f) => !hasValue(row[f]))
    : (row) => COST_FIELDS.every((f) => !hasValue(row[f]));
  const all = new Set();
  const missing = new Set();
  const missingRows = [];
  rows.forEach((row) => {
    if (!row || isSelfPair(row)) return;
    const key = pairKey(row);
    all.add(key);
    if (rowIsMissing(row)) {
      missing.add(key);
      missingRows.push(row);
    }
  });
  const totalPairs = all.size;
  const missingPairs = missing.size;
  return { totalPairs, missingPairs, partial: missingPairs > 0 && missingPairs < totalPairs, missingRows };
}

/**
 * Why rows are still empty, in the words the calculation gave (validationMessage),
 * most common first, without repeats. Empty when the calculation said nothing.
 *
 * @param {Array<object>} rows - rows still missing
 * @returns {string[]}
 */
export function missingTravelCostReasons(rows) {
  const counts = new Map();
  (rows || []).forEach((row) => {
    const messages = Array.isArray(row && row.validationMessage) ? row.validationMessage : [row && row.validationMessage];
    messages.filter((m) => typeof m === 'string' && m.trim()).forEach((m) => counts.set(m, (counts.get(m) || 0) + 1));
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([m]) => m);
}

/** Row identity for merging: pair plus vehicle type (by id, or by name on rows that only carry the name). */
export const travelCostRowKey = (row) => `${pairKey(row)}\u0000${(row && (row.vehicleTypeId || row.vehicleType)) || ''}`;

/**
 * A freshly calculated matrix with every value that was already there kept.
 *
 * For "Calculate missing and solve". Merged field by field: a pair that had a
 * distance but no travel time keeps its distance and takes the calculated time.
 * A value someone typed or uploaded is never replaced. Where a field is kept, its
 * method tag (costMethod) is kept with it. A row whose every field was kept is
 * the previous row unchanged.
 *
 * @param {Array<object>} previousRows - the table before calculating
 * @param {Array<object>} calculatedRows - the rows the calculation returned
 * @param {object} [costs] - generalSettings.costs, to derive an empty cost from a
 *   kept distance or travel time
 * @param {Array<object>} [vehicleTypes] - data.vehicle_types, so a multiplier for a
 *   deleted vehicle type is ignored as the request ignores it
 * @returns {Array<object>}
 */
export function keepExistingTravelCosts(previousRows, calculatedRows, costs, vehicleTypes) {
  if (!Array.isArray(previousRows) || previousRows.length === 0) return calculatedRows;
  const previousByKey = new Map();
  previousRows.forEach((row) => {
    if (row) previousByKey.set(travelCostRowKey(row), row);
  });
  const metric = costs && costs.costMetric;
  return calculatedRows.map((row) => {
    const previous = previousByKey.get(travelCostRowKey(row));
    if (!previous) return row;
    const keptFields = COST_FIELDS.filter((f) => hasValue(previous[f]));
    if (keptFields.length === 0) return row;
    if (keptFields.length === COST_FIELDS.length) return previous;
    const merged = { ...row };
    const methods = { ...(row.costMethod || {}) };
    keptFields.forEach((f) => {
      merged[f] = previous[f];
      if (previous.costMethod && previous.costMethod[f]) methods[f] = previous.costMethod[f];
      else delete methods[f];
    });
    /*
    A cost follows the value it is calculated from. The calculation derived its
    cost from its own distance (or travel time), but when that value was kept
    (someone typed 9), the calculated cost describes a different trip, and the
    solver, which routes on cost, would silently ignore the 9. So an empty cost
    is derived from the kept value, scaled as the backend scales it.
    */
    if ((metric === 'distance' || metric === 'duration')
        && keptFields.includes(metric) && !keptFields.includes('cost')) {
      const kept = Number(previous[metric]);
      if (Number.isFinite(kept)) {
        merged.cost = kept * costMultiplierFor(costs, row, vehicleTypes);
        if (previous.costMethod && previous.costMethod[metric]) methods.cost = previous.costMethod[metric];
        else delete methods.cost;
      } else {
        // Not a number ("1,234"): no cost can come from it, and the calculated
        // one belongs to a different value. Left empty, with the reason, so Run
        // names the gap instead of routing on a cost nobody chose.
        merged.cost = null;
        delete methods.cost;
        merged.validationMessage = [NOT_A_NUMBER_MESSAGE[metric]];
      }
    }
    merged.costMethod = methods;
    return merged;
  });
}

const NOT_A_NUMBER_MESSAGE = {
  distance: "The distance isn't a number, so no cost can be calculated from it.",
  duration: "The travel time isn't a number, so no cost can be calculated from it.",
};

/**
 * The multiplier the backend applies to a row's metric value to get its cost:
 * the vehicle type's own when costs depend on vehicle type and it has one,
 * otherwise the base multiplier. Mirrors getCostSettings, which sends only the
 * multipliers of vehicle types still in the table: one saved for a deleted type
 * is ignored there, so it is ignored here.
 *
 * @param {object} costs - generalSettings.costs
 * @param {object} row - a travel-cost row
 * @param {Array<object>} [vehicleTypes] - data.vehicle_types; when given, only
 *   their multipliers count
 * @returns {number}
 */
export function costMultiplierFor(costs, row, vehicleTypes) {
  // Costs entered by hand: multipliers are off, and the request sends 1.
  if (multipliersOff(costs)) return 1;
  const multipliers = (costs && costs.costMultiplier) || {};
  const base = Number.isFinite(Number(multipliers.all)) ? Number(multipliers.all) : 1;
  if (!costs || !costs.vehicleTypeDependence || !row) return base;
  if (Array.isArray(vehicleTypes) && !vehicleTypes.some((vt) => vt && vt.UUID === row.vehicleTypeId)) return base;
  const own = multipliers.vehicleTypes && multipliers.vehicleTypes[row.vehicleTypeId];
  return Number.isFinite(Number(own)) && own !== null && own !== '' ? Number(own) : base;
}

/**
 * Whether a /cost_matrix response puts a cost into at least one trip: any cost
 * for a full recalculation, and for "Calculate missing" (fillMissingOnly) a cost
 * where the table had none, after the same merge the reducer does.
 *
 * @param {Array<object>} previousRows - the table before calculating
 * @param {Array<object>} responseRows - travelCostResponses
 * @param {object} options
 * @param {boolean} [options.fillMissingOnly]
 * @param {object} [options.costs] - generalSettings.costs
 * @param {Array<object>} [options.vehicleTypes] - data.vehicle_types
 * @returns {boolean}
 */
export function costsFilledBy(previousRows, responseRows, { fillMissingOnly = false, costs, vehicleTypes } = {}) {
  const trips = (responseRows || []).filter((row) => row && !isSelfPair(row));
  if (!fillMissingOnly) return trips.some((row) => hasValue(row.cost));
  const previousByKey = new Map();
  (previousRows || []).forEach((row) => { if (row) previousByKey.set(travelCostRowKey(row), row); });
  const merged = keepExistingTravelCosts(previousRows, trips, costs, vehicleTypes);
  return merged.some((row) => {
    const previous = previousByKey.get(travelCostRowKey(row));
    return hasValue(row.cost) && Number.isFinite(Number(row.cost)) && !(previous && hasValue(previous.cost));
  });
}

/*
Costs calculated at two scales.

With costs entered by hand, multipliers are off and new costs use ×1 (see
multipliersOff). Costs calculated earlier, while costs were automatic, carry the
multiplier of that time. "Calculate missing" then fills new pairs at ×1 beside old
ones at, say, ×1.5, and the solver sees the new stops as a third cheaper.

A cost counts as calculated only while both its cost and its metric value carry
their method tags: editing a field drops its tag (editRowHelper), so a cost
someone typed over, or a distance they changed, is never treated as calculated
and never rescaled. Its scale is then cost ÷ value. A mix is flagged only when
some calculated costs are at an old scale and some at the one in use now.
*/

/**
 * Calculated costs at a different scale from the one in use now, when some are
 * at the current scale too; otherwise null. Only in manual entry.
 *
 * @param {Array<object>} rows - data.travel_costs
 * @param {object} costs - generalSettings.costs
 * @param {Array<object>} [vehicleTypes] - data.vehicle_types
 * @returns {{ uuids: string[], count: number, multipliers: number[], fromSavedMultipliers: boolean }|null}
 *   `fromSavedMultipliers`: every old scale matches a saved multiplier (base or a
 *   vehicle type's), so the numbers can be quoted; otherwise they came from some
 *   other setting (e.g. the cost metric changed since) and aren't.
 */
export function mixedCostScales(rows, costs, vehicleTypes) {
  if (!multipliersOff(costs)) return null;
  const metric = costs.costMetric;
  const uuids = [];
  const multipliers = new Set();
  let currentScale = false;
  let fromSavedMultipliers = true;
  const same = (a, b) => Math.abs(a - b) <= 1e-6 * Math.max(1, Math.abs(b));
  const saved = [
    costs.costMultiplier && costs.costMultiplier.all,
    ...Object.values((costs.costMultiplier && costs.costMultiplier.vehicleTypes) || {}),
  ].map(Number).filter(Number.isFinite);
  (rows || []).forEach((row) => {
    if (!row || isSelfPair(row)) return;
    const tags = row.costMethod || {};
    if (!tags.cost || !tags[metric] || !hasValue(row.cost) || !hasValue(row[metric])) return;
    const value = Number(row[metric]);
    const cost = Number(row.cost);
    if (!Number.isFinite(value) || !Number.isFinite(cost) || value === 0) return;
    const current = costMultiplierFor(costs, row, vehicleTypes);
    const scale = cost / value;
    // Within tolerance, or the same at the 6 significant digits the dialog would
    // show: never "calculated with ×1 … newer costs use ×1".
    if (same(scale, current) || Number(scale.toPrecision(6)) === Number(current.toPrecision(6))) {
      currentScale = true;
      return;
    }
    uuids.push(row.UUID);
    multipliers.add(Number(scale.toPrecision(6)));
    if (!saved.some((m) => same(scale, m))) fromSavedMultipliers = false;
  });
  if (uuids.length === 0 || !currentScale) return null;
  return { uuids, count: uuids.length, multipliers: [...multipliers].sort((a, b) => a - b), fromSavedMultipliers };
}

/**
 * What the Run dialog says about costs at two scales.
 *
 * @param {{ count: number, multipliers: number[] }} mixed - from mixedCostScales
 * @returns {string}
 */
export function mixedCostScalesMessage(mixed) {
  const { count, multipliers, fromSavedMultipliers = true } = mixed;
  const times = multipliers.map((m) => `×${m}`);
  let which;
  // A scale that matches none of the saved multipliers (the metric changed since,
  // say) is not quoted as if it were one.
  if (!fromSavedMultipliers) which = 'from a different cost setting';
  else if (times.length === 1) which = `with your ${times[0]} cost multiplier`;
  else which = `with your cost multipliers (${times.slice(0, -1).join(', ')} and ${times[times.length - 1]})`;
  let effect = 'cheaper or more expensive';
  if (multipliers.every((m) => m > 1)) effect = 'cheaper';
  else if (multipliers.every((m) => m < 1)) effect = 'more expensive';
  return `${count} travel ${count === 1 ? 'cost was' : 'costs were'} calculated earlier ${which}, `
    + "but multipliers aren't applied while costs are entered by hand, so newer costs use ×1. "
    + `Mixing the two would make some trips look ${effect} than they are.`;
}

/**
 * The table with those calculated costs put on the current scale: each one
 * recalculated from its own distance (or travel time) × the multiplier in use now.
 * Checked again here, so a row changed since the dialog opened is left alone.
 *
 * @param {Array<object>} rows - data.travel_costs
 * @param {string[]} uuids - from mixedCostScales
 * @param {object} costs - generalSettings.costs
 * @param {Array<object>} [vehicleTypes] - data.vehicle_types
 * @returns {Array<object>}
 */
export function rescaleCalculatedCosts(rows, uuids, costs, vehicleTypes) {
  const mixed = mixedCostScales(rows, costs, vehicleTypes);
  if (!mixed) return rows;
  const wanted = new Set((uuids || []).filter((id) => mixed.uuids.includes(id)));
  const metric = costs.costMetric;
  return rows.map((row) => (row && wanted.has(row.UUID)
    ? { ...row, cost: Number(row[metric]) * costMultiplierFor(costs, row, vehicleTypes) }
    : row));
}

/** Marked on a row whose value survived a recalculation that couldn't measure it. */
export const KEPT_VALUE_MESSAGE = "Kept the previous value; this pair couldn't be recalculated.";

/**
 * A recalculated matrix that never replaces a value with nothing.
 *
 * For "Populate travel costs" and "Recalculate everything": a calculated value
 * replaces the existing one, but where the calculation came back empty (a pair
 * it couldn't measure) the existing value is kept, with its method tag.
 *
 * A kept value is flagged (`Valid: 'warning'`, with KEPT_VALUE_MESSAGE) and
 * marked `keptFromPrevious`. It may be stale — someone can move a location
 * without clearing its costs — so it must not pass as freshly calculated, and the
 * toast counts these separately.
 *
 * @param {Array<object>} previousRows - the table before calculating
 * @param {Array<object>} calculatedRows - the rows the calculation returned
 * @param {Set<string>} [keptPairKeys] - filled with the pairs this response fell
 *   back on, so the message counts only what just happened rather than every row
 *   still carrying the flag from an earlier calculation.
 * @returns {Array<object>}
 */
export function preferCalculatedTravelCosts(previousRows, calculatedRows, keptPairKeys) {
  if (!Array.isArray(previousRows) || previousRows.length === 0) return calculatedRows;
  const previousByKey = new Map();
  previousRows.forEach((row) => {
    if (row) previousByKey.set(travelCostRowKey(row), row);
  });
  return calculatedRows.map((row) => {
    const previous = previousByKey.get(travelCostRowKey(row));
    if (!previous) return row;
    const fallBack = COST_FIELDS.filter((f) => !hasValue(row[f]) && hasValue(previous[f]));
    if (fallBack.length === 0) return row;
    const keptEverything = COST_FIELDS.every((f) => !hasValue(row[f]));
    const merged = keptEverything ? { ...previous } : { ...row };
    if (!keptEverything) {
      const methods = { ...(row.costMethod || {}) };
      fallBack.forEach((f) => {
        merged[f] = previous[f];
        if (previous.costMethod && previous.costMethod[f]) methods[f] = previous.costMethod[f];
        else delete methods[f];
      });
      merged.costMethod = methods;
    }
    merged.keptFromPrevious = true;
    merged.Valid = 'warning';
    merged.validationMessage = [KEPT_VALUE_MESSAGE];
    if (keptPairKeys && !isSelfPair(row)) keptPairKeys.add(pairKey(row));
    return merged;
  });
}

/**
 * After a calculation: how many location pairs (not rows, and never self-pairs)
 * came back empty, kept an older value because they couldn't be recalculated, or
 * were estimated straight-line as a fallback.
 *
 * @param {Array<object>} rows - the table after the calculation
 * @param {Set<string>} [keptPairKeys] - the pairs this response fell back on; when
 *   given, only those count as kept. A row keeps its flag until it is calculated
 *   again, so without this a later "Calculate missing" would re-report pairs it
 *   never tried.
 * @returns {{ totalPairs: number, emptyPairs: number, keptPairs: number, estimatedPairs: number }}
 */
export function calculatedTravelCostProblems(rows, keptPairKeys) {
  const all = new Set();
  const empty = new Set();
  const kept = new Set();
  const estimated = new Set();
  (rows || []).forEach((row) => {
    if (!row || isSelfPair(row)) return;
    const key = pairKey(row);
    all.add(key);
    if (COST_FIELDS.every((f) => !hasValue(row[f]))) {
      empty.add(key);
    } else if (row.keptFromPrevious) {
      /*
      A kept row carries Valid 'warning' and keeps it until it is calculated
      again, so it must never fall through to the straight-line bucket: that
      said "couldn't be measured along real roads" about pairs this calculation
      never even requested. It counts as kept only when this response fell back
      on it; otherwise it counts as nothing at all.
      */
      if (keptPairKeys ? keptPairKeys.has(key) : true) kept.add(key);
    } else if (row.Valid === 'warning') {
      estimated.add(key);
    }
  });
  return { totalPairs: all.size, emptyPairs: empty.size, keptPairs: kept.size, estimatedPairs: estimated.size };
}

/**
 * The words used, in the dialog and when a run is stopped, for pairs a
 * calculation couldn't fill.
 *
 * @param {number} missingPairs
 * @param {number} totalPairs
 * @param {string} [reason] - the calculation's own message, if any
 * @param {object} [options]
 * @param {boolean} [options.solvable=true] - false when no trip is usable, so
 *   no "Solve anyway" is offered and the message says why
 * @returns {string}
 */
export function stillMissingMessage(missingPairs, totalPairs, reason, { solvable = true } = {}) {
  return `We couldn't calculate ${missingPairs} of ${totalPairs} location pairs${reason ? ` (${reason})` : ''}. `
    + (solvable ? MISSING_TRAVEL_COST_CONSEQUENCE : NO_ROUTABLE_TRIPS_MESSAGE);
}

const METHOD_WORDS = { driving: 'driving', geo: 'straight-line', euclidean: 'straight-line', manhattan: 'Manhattan' };
const QUANTITY_WORDS = { distance: 'distances', duration: 'travel times' };

/**
 * When filling gaps would mix methods: the sentence saying so, or null.
 *
 * Calculated rows carry `costMethod` ({ distance, duration }: the method each
 * field was calculated with). Compared, for each needed field, with the method
 * the next calculation will use. Rows without a tag (typed or uploaded) say
 * nothing about method and are ignored.
 *
 * @param {Array<object>} rows - data.travel_costs
 * @param {string[]} fields - needed fields
 * @param {{distance?: string, duration?: string}} nextMethods - what a calculation now would use
 * @returns {string|null}
 */
export function mixedMethodNotice(rows, fields, nextMethods) {
  const parts = [];
  (fields || []).filter((f) => f === 'distance' || f === 'duration').forEach((field) => {
    const next = nextMethods && nextMethods[field];
    if (!next) return;
    // Only a field that is actually missing somewhere will be calculated: a
    // complete one can't end up mixed.
    if (!(rows || []).some((row) => row && !isSelfPair(row) && !hasValue(row[field]))) return;
    const used = new Set();
    (rows || []).forEach((row) => {
      if (row && hasValue(row[field]) && row.costMethod && row.costMethod[field]) used.add(row.costMethod[field]);
    });
    const nextWord = METHOD_WORDS[next] || next;
    const others = [...used].filter((m) => (METHOD_WORDS[m] || m) !== nextWord);
    if (others.length > 0) {
      const usedWords = [...new Set(others.map((m) => METHOD_WORDS[m] || m))].join(' and ');
      parts.push(`the missing ${QUANTITY_WORDS[field]} will be ${nextWord}, but the existing ones are ${usedWords}`);
    }
  });
  if (parts.length === 0) return null;
  const sentence = parts.join('; ');
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

/**
 * Whether the scenario's cost metric is set to be calculated from coordinates,
 * rather than entered or uploaded by hand. A custom metric is always by hand.
 *
 * @param {object} present - inputData.present
 * @returns {boolean}
 */
export function costsAreAutoComputed(present) {
  const costs = present && present.generalSettings && present.generalSettings.costs;
  if (!costs || costs.costMetric === 'custom') return false;
  const autoCompute = costs.autoCompute || {};
  return autoCompute[costs.costMetric] !== false;
}

/**
 * Told after Run calculated travel costs itself, because the table was empty.
 * The calculation is silent otherwise, and people should know the numbers they
 * are about to see routes for weren't there a moment ago.
 */
export const AUTO_CALCULATED_COSTS_MESSAGE =
  "Your travel costs were empty, so we calculated them from your locations' coordinates, using your Cost settings.";

/**
 * How many location pairs a recalculation replaced a HAND-ENTERED value in.
 *
 * Used where a recalculation runs on the person's behalf (the cartesian
 * pre-solve refresh). Replacing a number the app itself calculated a moment ago
 * is housekeeping and worth nothing to anyone; replacing one that was typed or
 * uploaded is a change they didn't ask for. Calculated fields carry a
 * `costMethod` tag, typed and uploaded ones don't, so only untagged fields
 * count. Pairs left untouched, and pairs that had no value, don't count either.
 *
 * @param {Array<object>} previousRows
 * @param {Array<object>} nextRows
 * @returns {number}
 */
export function countReplacedTravelCostPairs(previousRows, nextRows) {
  if (!Array.isArray(previousRows) || !Array.isArray(nextRows)) return 0;
  const nextByKey = new Map();
  nextRows.forEach((row) => {
    if (row && !isSelfPair(row)) nextByKey.set(travelCostRowKey(row), row);
  });
  const changed = new Set();
  previousRows.forEach((row) => {
    if (!row || isSelfPair(row)) return;
    const next = nextByKey.get(travelCostRowKey(row));
    if (!next) return;
    const replaced = COST_FIELDS.some((f) => hasValue(row[f]) && hasValue(next[f]) && row[f] !== next[f]
      && !(row.costMethod && row.costMethod[f]));
    if (replaced) changed.add(pairKey(row));
  });
  return changed.size;
}

/**
 * How many of the matrix's measured travel times are straight-line estimates
 * rather than road-network times.
 *
 * A mixed matrix is the interesting case: one road-network row used to silence
 * the note entirely, so a schedule that was three-quarters estimated read as
 * exact. Rows with no method tag (typed or uploaded) say nothing either way and
 * are not counted.
 *
 * @param {Array<object>} rows
 * @returns {{ estimated: number, measured: number, all: boolean, some: boolean }}
 */
export function travelTimeEstimateSummary(rows) {
  let estimated = 0;
  let measured = 0;
  (rows || []).forEach((row) => {
    const method = row && row.costMethod && row.costMethod.duration;
    if (!method) return;
    measured += 1;
    if (method !== 'driving') estimated += 1;
  });
  return { estimated, measured, all: measured > 0 && estimated === measured, some: estimated > 0 };
}
